import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Stamp, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function LoyaltyProgramCard({ marketingActive }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [total, setTotal] = useState(0);
  const [geocoded, setGeocoded] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    visits_required: 10,
    reward_text: "",
    radius_m: 250,
    photo_prompt: "",
    strict_mode: false,
    strict_action: "warn",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/business/loyalty");
      setForm({
        enabled: !!data.config.enabled,
        visits_required: data.config.visits_required,
        reward_text: data.config.reward_text || "",
        radius_m: data.config.radius_m,
        photo_prompt: data.config.photo_prompt || "",
        strict_mode: !!data.config.strict_mode,
        strict_action: data.config.strict_action || "warn",
      });
      setTotal(data.total_checkins);
      setGeocoded(!!data.geocoded);
    } catch (e) {
      console.warn("Loyalty load failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (form.enabled && form.reward_text.trim().length < 4) {
      toast.error("Please describe the reward customers earn (e.g. 'Free coffee on us').");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/me/business/loyalty", form);
      setGeocoded(!!data.geocoded);
      toast.success(form.enabled ? "Loyalty program live" : "Loyalty program updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-md border border-border p-6" data-testid="loyalty-program-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Stamp size={18} className="text-[#B31E1E]" />
            <h3 className="font-display text-2xl">Loyalty stamps</h3>
            {!marketingActive && (
              <span className="px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                Marketing add-on
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Reward repeat customers with a visual stamp card. Customers self-check-in from your profile page — Servrevu verifies their GPS location and requires a fresh camera photo (no gallery uploads). One check-in per customer per 24h.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="overline text-gray-500">Total visits logged</div>
          <div className="font-display text-3xl mt-1" data-testid="loyalty-total-checkins">{total}</div>
        </div>
      </div>

      {!marketingActive && (
        <div className="mt-5 border border-dashed border-amber-300 bg-amber-50/50 rounded-md p-4 text-sm">
          <p>The loyalty program is part of the Marketing add-on ($35/mo, on top of servrevu Pro).</p>
          <Link to="/subscription">
            <Button className="mt-3 rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="loyalty-upgrade-cta">
              Add Marketing — $35/mo →
            </Button>
          </Link>
        </div>
      )}

      {marketingActive && (
        <div className="mt-5 space-y-5">
          <div className="flex items-center justify-between border border-border rounded-md p-4 bg-gray-50/50">
            <div className="min-w-0">
              <Label htmlFor="loy-enabled" className="text-sm font-medium">Enable loyalty program</Label>
              <p className="text-xs text-gray-500 mt-1">Customers will see a "Check in for loyalty stamp" button on your profile.</p>
            </div>
            <Switch
              id="loy-enabled"
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: !!v })}
              data-testid="loyalty-enabled-toggle"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="loy-visits">Visits required to earn a reward</Label>
              <Input
                id="loy-visits"
                type="number"
                min={2}
                max={50}
                step={1}
                value={form.visits_required}
                onChange={(e) => setForm({ ...form, visits_required: Number(e.target.value) || 10 })}
                className="rounded-md mt-1 max-w-[140px]"
                data-testid="loyalty-visits-input"
              />
            </div>
            <div>
              <Label htmlFor="loy-radius">GPS radius (meters)</Label>
              <Input
                id="loy-radius"
                type="number"
                min={50}
                max={2000}
                step={10}
                value={form.radius_m}
                onChange={(e) => setForm({ ...form, radius_m: Number(e.target.value) || 250 })}
                className="rounded-md mt-1 max-w-[140px]"
                data-testid="loyalty-radius-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">How close customers must be to check in. 250m is a good default.</p>
            </div>
          </div>

          <div>
            <Label htmlFor="loy-reward">Reward customers earn</Label>
            <Textarea
              id="loy-reward"
              rows={3}
              maxLength={300}
              placeholder="Free coffee on us — show this on your next visit."
              value={form.reward_text}
              onChange={(e) => setForm({ ...form, reward_text: e.target.value })}
              className="rounded-md mt-1"
              data-testid="loyalty-reward-input"
            />
            <p className="text-[11px] text-gray-500 mt-1">Shown on the customer's stamp card once they've filled it.</p>
          </div>

          <div>
            <Label htmlFor="loy-prompt">Check-in photo requirement</Label>
            <Textarea
              id="loy-prompt"
              rows={2}
              maxLength={200}
              placeholder="e.g. 'Your receipt OR your drink with our logo visible.'"
              value={form.photo_prompt}
              onChange={(e) => setForm({ ...form, photo_prompt: e.target.value })}
              className="rounded-md mt-1"
              data-testid="loyalty-photo-prompt-input"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Tell customers what to capture so the photo proves a purchase from your business. If left blank, a generic prompt is shown. You can dispute any check-in that doesn't comply.
            </p>
          </div>

          <div className="border border-border rounded-md p-4 bg-gray-50/50 space-y-3" data-testid="loyalty-strict-mode-block">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="loy-strict" className="text-sm font-medium inline-flex items-center gap-2">
                  AI photo verification
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-[#B31E1E] text-white">Strict mode</span>
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  Uses LLM Vision to check each check-in photo against your prompt above. Costs a small amount of credits per check-in.
                </p>
              </div>
              <Switch
                id="loy-strict"
                checked={form.strict_mode}
                onCheckedChange={(v) => setForm({ ...form, strict_mode: !!v })}
                data-testid="loyalty-strict-toggle"
              />
            </div>
            {form.strict_mode && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-gray-500">When a photo doesn&apos;t match</Label>
                <div className="mt-1.5 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, strict_action: "warn" })}
                    className={`px-3 py-1.5 rounded-[35px] text-xs font-medium border transition-colors ${
                      form.strict_action === "warn"
                        ? "bg-amber-100 border-amber-400 text-amber-800"
                        : "bg-white border-border text-gray-600 hover:bg-gray-50"
                    }`}
                    data-testid="loyalty-strict-action-warn"
                  >
                    Soft warn — allow & flag for review
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, strict_action: "block" })}
                    className={`px-3 py-1.5 rounded-[35px] text-xs font-medium border transition-colors ${
                      form.strict_action === "block"
                        ? "bg-rose-100 border-rose-400 text-rose-800"
                        : "bg-white border-border text-gray-600 hover:bg-gray-50"
                    }`}
                    data-testid="loyalty-strict-action-block"
                  >
                    Hard reject — block the check-in
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={save}
              disabled={saving || loading}
              className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="loyalty-save-button"
            >
              {saving ? "Saving…" : "Save loyalty settings"}
            </Button>
            {form.enabled && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-gray-500" data-testid="loyalty-geocode-status">
                <MapPin size={12} className={geocoded ? "text-emerald-600" : "text-amber-600"} />
                {geocoded ? "GPS location verified" : "Geocoding pending — saved address will be used"}
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
